import { useLayoutEffect, useRef } from 'react';

const START_SELECTOR = '[data-sticky-column-index]';
const END_SELECTOR = '[data-sticky-end-index]';
const START_OFFSET_PROPERTY = '--lp-data-table-sticky-start-';
const END_OFFSET_PROPERTY = '--lp-data-table-sticky-end-';
const START_EXTENT_PROPERTY = '--lp-data-table-sticky-start-extent';
const END_EXTENT_PROPERTY = '--lp-data-table-sticky-end-extent';

export type StickyColumnExtents = {
  startExtent: number;
  endExtent: number;
};

const NO_EXTENTS: StickyColumnExtents = { startExtent: 0, endExtent: 0 };

export const stickyStartOffset = (index: number) => `var(${START_OFFSET_PROPERTY}${index}, 0px)`;
export const stickyEndOffset = (index: number) => `var(${END_OFFSET_PROPERTY}${index}, 0px)`;
export const stickyStartExtent = `var(${START_EXTENT_PROPERTY}, 0px)`;
export const stickyEndExtent = `var(${END_EXTENT_PROPERTY}, 0px)`;

const headerColumns = (table: HTMLElement, selector: string) =>
  [...table.querySelectorAll<HTMLElement>(selector)].filter(
    (column) => column.getAttribute('role') === 'columnheader' || column.tagName === 'TH',
  );

const measureSide = (columns: HTMLElement[], indexOf: (column: HTMLElement) => number) => {
  const ordered = [...columns].sort((left, right) => indexOf(left) - indexOf(right));
  let nextOffset = 0;
  const offsets: number[] = [];
  for (const column of ordered) {
    offsets[indexOf(column)] = nextOffset;
    nextOffset += column.getBoundingClientRect().width;
  }
  return { offsets, extent: nextOffset };
};

function observeStickyColumnOffsets(
  table: HTMLElement,
  container: HTMLElement,
  _columnKey: string,
  setExtents: (extents: StickyColumnExtents) => void,
) {
  const startColumns = headerColumns(table, START_SELECTOR);
  const endColumns = headerColumns(table, END_SELECTOR);
  const written = new Map<string, string>();

  const write = (property: string, value: number) => {
    const next = `${value}px`;
    if (written.get(property) !== next) {
      written.set(property, next);
      container.style.setProperty(property, next);
    }
  };

  const measure = () => {
    const start = measureSide(startColumns, (column) => Number(column.dataset.stickyColumnIndex));
    const end = measureSide(endColumns, (column) => Number(column.dataset.stickyEndIndex));
    start.offsets.forEach((offset, index) => write(`${START_OFFSET_PROPERTY}${index}`, offset));
    end.offsets.forEach((offset, index) => write(`${END_OFFSET_PROPERTY}${index}`, offset));
    write(START_EXTENT_PROPERTY, start.extent);
    write(END_EXTENT_PROPERTY, end.extent);
    setExtents({ startExtent: start.extent, endExtent: end.extent });
  };

  measure();

  const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
  [...startColumns, ...endColumns].forEach((column) => observer?.observe(column));
  return () => {
    observer?.disconnect();
    written.forEach((_value, property) => container.style.removeProperty(property));
    setExtents(NO_EXTENTS);
  };
}

export function useStickyColumnOffsets(table: HTMLElement | null, container: HTMLElement | null, columnKey: string) {
  const extents = useRef<StickyColumnExtents>(NO_EXTENTS);

  useLayoutEffect(() => {
    if (!table || !container) {
      return;
    }
    return observeStickyColumnOffsets(table, container, columnKey, (next) => {
      extents.current = next;
    });
  }, [table, container, columnKey]);

  return extents;
}
